import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';

/**
 * Instagram Community CTA — sits directly after the fireworks slide.
 *
 * Left column:  QR code that opens the STIG ROCK Instagram profile,
 *               small official-style IG glyph and the @-handle.
 * Right column: BE PART OF THE MOVEMENT headline + community copy.
 */

// Localised copy — English retained for the marketing punchline
// (hashtags and IG handle stay identical across every language).
const COPY = {
  de: {
    overline: 'Community',
    title: 'WERDE TEIL DER BEWEGUNG',
    lead: 'Gemeinsam schreiben wir die Geschichte von STIG ROCK.',
    body: 'Teile deine Eindrücke, markiere @stigrockofficial und werde Teil unserer internationalen Community.',
  },
  en: {
    overline: 'Community',
    title: 'BE PART OF THE MOVEMENT',
    lead: 'Together we write the story of STIG ROCK.',
    body: 'Share your moments, tag @stigrockofficial and become part of our international community.',
  },
  tr: {
    overline: 'Topluluk',
    title: 'HAREKETİN PARÇASI OL',
    lead: 'STIG ROCK\u2019un hikâyesini birlikte yazıyoruz.',
    body: 'Anlarını paylaş, @stigrockofficial etiketle ve uluslararası topluluğumuzun bir parçası ol.',
  },
  hu: {
    overline: 'Közösség',
    title: 'LÉGY A MOZGALOM RÉSZE',
    lead: 'A STIG ROCK történetét együtt írjuk.',
    body: 'Oszd meg pillanataidat, jelöld be @stigrockofficial és légy a nemzetközi közösségünk része.',
  },
};

const IG_URL = 'https://instagram.com/stigrockofficial';

// Inline SVG of the official Instagram glyph (simple mark, monochrome).
function InstagramGlyph({ className = '' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke="currentColor" strokeWidth="1.7"/>
      <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.7"/>
      <circle cx="17.6" cy="6.4" r="1.1" fill="currentColor"/>
    </svg>
  );
}

export default function SectionInstagram() {
  const { lang } = useT();
  const c = COPY[lang] || COPY.en;

  return (
    <SectionShell id="instagram" className="relative overflow-hidden" fullBleed>
      <div className="ig-shell" data-testid="section-instagram">
        <div className="ig-grid">
          {/* LEFT — QR + handle */}
          <div className="ig-left">
            <Reveal>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="ig-qr-link"
                data-testid="ig-qr"
                aria-label="STIG ROCK Instagram"
              >
                <div className="ig-qr-frame">
                  <img
                    src="/qr-instagram.png"
                    alt="Instagram QR — @stigrockofficial"
                    loading="lazy"
                    draggable={false}
                    className="ig-qr-img"
                  />
                </div>
              </a>
            </Reveal>

            <Reveal delay={160}>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="ig-handle-row"
                data-testid="ig-handle"
              >
                <InstagramGlyph className="ig-glyph" />
                <span className="ig-handle-text">@stigrockofficial</span>
              </a>
            </Reveal>
          </div>

          {/* RIGHT — copy */}
          <div className="ig-right">
            <Reveal>
              <div className="overline ig-overline" data-testid="ig-overline">
                {c.overline}
              </div>
            </Reveal>

            <Reveal delay={140}>
              <h2 className="ig-title font-display" data-testid="ig-title">
                {c.title}
              </h2>
            </Reveal>

            <Reveal delay={220}>
              <p className="ig-lead font-ui" data-testid="ig-lead">
                {c.lead}
              </p>
            </Reveal>

            <Reveal delay={300}>
              <p className="ig-body font-ui" data-testid="ig-body">
                <span className="ig-body-icon" aria-hidden="true">
                  <InstagramGlyph className="ig-body-glyph" />
                </span>
                <span>{c.body}</span>
              </p>
            </Reveal>

            <Reveal delay={380}>
              <div className="ig-hashtags" data-testid="ig-hashtags">
                <span>#STIGROCK</span>
                <span className="ig-hashtags-divider">·</span>
                <span>#BuildLegacy</span>
                <span className="ig-hashtags-divider">·</span>
                <span>#OwnValue</span>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
